import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import last from "lodash/last";
import pickBy from "lodash/pickBy";
import {
  FIELD_ASSIGNEE_ID_KEY,
  FIELD_ATTACHMENT_IDS_KEY,
  FIELD_AUDIENCES_KEY,
  FIELD_AVAILABILITY_KEY,
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_COMMUNICATION_TYPE_KEY,
  FIELD_DESCRIPTION_KEY,
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_INTEREST_STATEMENT_ENABLED_KEY,
  FIELD_MIN_REQUIRED_COUNT_KEY,
  FIELD_NAME_KEY,
  FIELD_PARENT_ACTIVITY_KEY,
  FIELD_POST_UNTIL_CHANGE_REASON_KEY,
  FIELD_POST_UNTIL_KEY,
  FIELD_PRIVACY_KEY,
  FIELD_SKILLS_FRAMEWORK_IDS_KEY,
  FIELD_SUBTEMPLATE_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { getPrivacyConfiguration } from "PFApp/helpers/activity_form/get_privacy_configuration";
import { FormSchema, Sections } from "PFApp/types/activity_form/activity_form_schema";
import useIsAvailabilityPermitted from "PFApp/use_is_availability_permitted";
import useIsChatEnabled from "PFApp/use_is_chat_enabled";
import { getVisibleProperties } from "PFCore/helpers/templates";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { Activity, FeatureFlag, Profile, Subtemplate, Template } from "PFCore/types";
import { useCallback, useMemo } from "react";

import { useGroupedProperties } from "./use_grouped_properties";
import { useIsBookingCategoryVisible } from "./use_is_booking_category_visible";

type UseFormSectionsGeneratorProps = {
  isSubtemplateSectionVisible: boolean;
  template: Template;
  subtemplate: Subtemplate | null;
  activityCreator: Profile | null;
  parentActivity: Activity | undefined;
  isRole: boolean;
  isEngagement: boolean;
  isEngagementVisible: boolean;
  isDraft: boolean;
  isNew: boolean;
  isQuestionsSectionVisible: boolean;
};

export const useFormSectionsGenerator = ({
  isSubtemplateSectionVisible,
  template,
  subtemplate,
  activityCreator,
  parentActivity,
  isRole,
  isEngagement,
  isEngagementVisible,
  isDraft,
  isNew,
  isQuestionsSectionVisible
}: UseFormSectionsGeneratorProps) => {
  const isFeatureEnabled = useIsFeatureEnabled();
  const isSegmentEnabled = isFeatureEnabled(FeatureFlag.Segments);
  const isSkillsFrameworksEnabled = isFeatureEnabled(FeatureFlag.SkillsFramework);

  const isBookingCategoryVisible = useIsBookingCategoryVisible({ template, activityCreator });
  const isAvailabilityPermitted = useIsAvailabilityPermitted();
  const hasTemplateWithAvailability = useMemo(() => {
    const properties = getVisibleProperties(template);
    return !!properties.find((item) => item.name === "availability");
  }, [template]);

  const { attributesProperties, matchAttributesProperties, segmentProperties } = useGroupedProperties(
    template,
    subtemplate,
    activityCreator
  );
  const { isForceVisibilitySet, isForcePrivate } = getPrivacyConfiguration({ template, parentActivity });

  const isAvailabilitySectionVisible = isAvailabilityPermitted && hasTemplateWithAvailability;
  const isAttachmentsSectionVisible = template.schema?.rules?.hide_attachments !== true;
  const isPrivacySectionVisible = !isForceVisibilitySet;

  const isChatEnabled = useIsChatEnabled();
  const isConversationSectionVisible = isChatEnabled && !isForcePrivate && (isDraft || isNew);

  const generateFormSections = useCallback(() => {
    const sectionRoleType = isSubtemplateSectionVisible ? [{ name: FIELD_SUBTEMPLATE_KEY }] : [];

    if (isSubtemplateSectionVisible && !subtemplate?.subtemplate_key) {
      return {
        [Sections.Subtemplate]: sectionRoleType
      };
    }

    const sectionBasicInformation = compact([
      { name: FIELD_NAME_KEY },
      { name: FIELD_DESCRIPTION_KEY },
      { name: FIELD_POST_UNTIL_KEY },
      { name: FIELD_POST_UNTIL_CHANGE_REASON_KEY },
      isRole && { name: FIELD_MIN_REQUIRED_COUNT_KEY },
      isEngagementVisible && { name: FIELD_PARENT_ACTIVITY_KEY },
      (isEngagement || isRole) && { name: FIELD_ASSIGNEE_ID_KEY },
      isBookingCategoryVisible && { name: FIELD_BOOKING_CATEGORY_ID_KEY },
      ...(isSegmentEnabled
        ? segmentProperties.map((property) => ({ name: property.name, segment: true, property }))
        : [])
    ]);
    const sectionSkillsFrameworks =
      isSkillsFrameworksEnabled && isRole ? [{ name: FIELD_SKILLS_FRAMEWORK_IDS_KEY }] : [];
    const sectionMatchAttributes = matchAttributesProperties.map((property) => ({
      name: property.name,
      property,
      match: true
    }));
    const sectionAvailability = isAvailabilitySectionVisible && [
      { name: last(FIELD_AVAILABILITY_KEY.split(".")) }
    ];
    const sectionAttributes = attributesProperties.map((property) => ({ name: property.name, property }));
    const sectionAttachments = isAttachmentsSectionVisible && [{ name: FIELD_ATTACHMENT_IDS_KEY }];
    const sectionPrivacy = isPrivacySectionVisible && [
      { name: FIELD_PRIVACY_KEY },
      { name: FIELD_AUDIENCES_KEY }
    ];
    const sectionConversation = isConversationSectionVisible && [{ name: FIELD_COMMUNICATION_TYPE_KEY }];
    const sectionQuestions = isQuestionsSectionVisible && [
      { name: FIELD_INTEREST_STATEMENT_ENABLED_KEY },
      { name: FIELD_INTEREST_QUESTIONS_KEY }
    ];

    const schema: FormSchema = pickBy(
      {
        [Sections.Subtemplate]: sectionRoleType,
        [Sections.BasicInformation]: sectionBasicInformation,
        [Sections.SkillsFrameworks]: sectionSkillsFrameworks,
        [Sections.MatchAttributes]: sectionMatchAttributes,
        [Sections.Availability]: sectionAvailability,
        [Sections.Attributes]: sectionAttributes,
        [Sections.Attachments]: sectionAttachments,
        [Sections.Privacy]: sectionPrivacy,
        [Sections.Conversation]: sectionConversation,
        [Sections.ShowInterestQuestions]: sectionQuestions
      },
      (value) => !isEmpty(value)
    );

    return schema;
  }, [
    isSubtemplateSectionVisible,
    subtemplate,
    isRole,
    isEngagement,
    isEngagementVisible,
    isBookingCategoryVisible,
    isQuestionsSectionVisible,
    isSegmentEnabled,
    segmentProperties,
    isSkillsFrameworksEnabled,
    matchAttributesProperties,
    isAvailabilitySectionVisible,
    attributesProperties,
    isAttachmentsSectionVisible,
    isPrivacySectionVisible,
    isConversationSectionVisible
  ]);

  return {
    generateFormSections
  };
};
